import React from 'react';

import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import type { LensCardType } from '~/types/leLens';

import { type StyleTabState, currencies, formatKinds } from './useStyleTabState';

type VisualBasicsProps = { state: StyleTabState; type: LensCardType; busy?: boolean };

/** Everyday presentation choices, kept together in Appearance. */
export default function VisualBasics({ state, type, busy = false }: VisualBasicsProps) {
  if (!state.showsValueFormat && !state.showsCommentary) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Display</Typography>
      {state.showsValueFormat && (
        <Stack direction="row" spacing={1}>
          <FormControl fullWidth size="small">
            <InputLabel id="le-lens-quick-format-kind">Values</InputLabel>
            <Select
              disabled={busy}
              label="Values"
              labelId="le-lens-quick-format-kind"
              onChange={(event) => state.setKind(event.target.value)}
              value={state.kind}
            >
              {formatKinds.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="le-lens-quick-format-decimals">Decimals</InputLabel>
            <Select
              disabled={busy}
              label="Decimals"
              labelId="le-lens-quick-format-decimals"
              onChange={(event) => state.setDecimals(event.target.value)}
              value={state.decimals}
            >
              <MenuItem value="auto">Auto</MenuItem>
              {[0, 1, 2, 3, 4].map((value) => (
                <MenuItem key={value} value={String(value)}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}
      {state.showsValueFormat && state.kind === 'currency' && (
        <FormControl fullWidth size="small">
          <InputLabel id="le-lens-quick-currency">Currency</InputLabel>
          <Select
            disabled={busy}
            label="Currency"
            labelId="le-lens-quick-currency"
            onChange={(event) => state.setCurrency(event.target.value)}
            value={state.currency}
          >
            {currencies.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {state.showsValueFormat && state.kind === 'percent' && (
        <FormControl fullWidth size="small">
          <InputLabel id="le-lens-quick-percent-scale">Values are</InputLabel>
          <Select
            disabled={busy}
            label="Values are"
            labelId="le-lens-quick-percent-scale"
            onChange={(event) => state.setPercentScale(event.target.value)}
            value={state.percentScale}
          >
            <MenuItem value="percent">Already 0–100</MenuItem>
            <MenuItem value="unit">A fraction, 0–1</MenuItem>
          </Select>
        </FormControl>
      )}
      {state.showsCommentary && (
        <Stack spacing={1}>
          <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography sx={{ fontWeight: 600 }} variant="body2">
                Commentary
              </Typography>
              <Typography color="text.secondary" variant="caption">
                A short reading of this chart
              </Typography>
            </Box>
            <Switch
              checked={state.commentaryVisible}
              disabled={busy}
              onChange={(event) => state.setCommentaryVisible(event.target.checked)}
              slotProps={{ input: { 'aria-label': 'Show commentary' } }}
            />
          </Stack>
          {state.commentaryVisible && type !== 'metric' && (
            <FormControl fullWidth size="small">
              <InputLabel id="le-lens-commentary-position">Commentary position</InputLabel>
              <Select
                disabled={busy}
                label="Commentary position"
                labelId="le-lens-commentary-position"
                onChange={(event) => state.setCommentaryPosition(event.target.value)}
                value={state.commentaryPosition}
              >
                <MenuItem value="right">Right of chart</MenuItem>
                <MenuItem value="below">Below chart</MenuItem>
              </Select>
              <FormHelperText>Moves below the chart on narrow screens.</FormHelperText>
            </FormControl>
          )}
        </Stack>
      )}
    </Stack>
  );
}
